import Phaser from "phaser";
import { GAME, HERO, LAYOUT } from "@/config";
import { ALUMNI } from "@/data/tunables";
import { PALETTE_HEX } from "@/palette";
import { buildHero, fitFacilityName, HERO_THEME } from "./hero";
import { wirePan } from "./pan";
import { artTextureKey, isArtLoaded, resolveAlumniTexture } from "@/sprites/artManifest";
import { ALUM_BBQ_H, ALUM_BBQ_TOP, ALUM_HALL_H, ALUM_TOP, ALUM_WORLD_H } from "@/data/visuals";
import { ALUMNI_TESTIMONIAL, OWNER_ALUMNI_PITCH, TAP_GAGS } from "@/data/tapGags";
import type { TickEvent } from "@/systems/engine";
import { runtime } from "@/systems/runtime";
import { bakeAllCharacterTextures, Character } from "@/sprites/Character";
import { installAlumniActions, showGagModal, type OverlayHandles } from "@/ui/dom";

/**
 * THE ALUMNI NETWORK (v0.2 RFC §6/§8; v0.9 dollhouse ruled 2026-07-20: both
 * floors BIG, "fitting of making it this far"). Two-band world: the Grand
 * Hall (the Wall of Sobriety — the trophy lobby that is also the brochure)
 * over the BBQ lawn (grill, podium testimonial, the owner working the
 * crowd). The roster mills on the lawn — the only characters you don't
 * control; the absence of UI over them is the point.
 */

/** Lawn band the crowd wanders (world y, inside the BBQ panel's grass). */
const LAWN_TOP = ALUM_BBQ_TOP + Math.round(0.60 * ALUM_BBQ_H);
const LAWN_BOTTOM = ALUM_BBQ_TOP + Math.round(0.93 * ALUM_BBQ_H);
/** Art-absent fallback band: the old on-screen floor strip (no pan). */
const FALLBACK_TOP = LAYOUT.floorY - 260;
const FALLBACK_BOTTOM = LAYOUT.floorY;

export class AlumniScene extends Phaser.Scene {
  private overlay!: OverlayHandles;
  private walkers: Character[] = [];
  private speaker: Phaser.GameObjects.Image | null = null;
  /** Which alum holds the podium — the speaker follows roster[0] (destroy +
   * recreate on leader change; CodeRabbit leg-4 Major, PR #20). */
  private speakerId: number | null = null;
  private countText: Phaser.GameObjects.Text | null = null;
  private facilityBar: Phaser.GameObjects.Text | null = null;
  private unsubscribe: (() => void) | undefined;
  /** Panels present? Without them there is no pan — the crowd must live in
   * the fixed viewport (art-absent fallback, leg-1/codex converged finding). */
  private dollhouseActive = false;

  constructor() {
    super("alumni");
  }

  init(data: { overlay: OverlayHandles }): void {
    this.overlay = data.overlay;
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE_HEX.cream);
    bakeAllCharacterTextures(this);
    this.walkers = [];
    this.speaker = null;
    this.speakerId = null;
    // create() re-runs on scene restart — one-shot loop guards must reset.
    this.testimonialRunning = false;
    this.pitchRunning = false;

    const dollhouse = isArtLoaded("alumni-floor-hall") && isArtLoaded("alumni-floor-bbq");
    this.dollhouseActive = dollhouse;
    if (dollhouse) {
      this.add
        .image(GAME.width / 2, ALUM_TOP + ALUM_HALL_H, artTextureKey("alumni-floor-hall"))
        .setOrigin(0.5, 1)
        .setDisplaySize(GAME.width, ALUM_HALL_H)
        .setDepth(-100);
      this.add
        .image(GAME.width / 2, ALUM_BBQ_TOP + ALUM_BBQ_H, artTextureKey("alumni-floor-bbq"))
        .setOrigin(0.5, 1)
        .setDisplaySize(GAME.width, ALUM_BBQ_H)
        .setDepth(-100);
      this.add
        .rectangle(GAME.width / 2, ALUM_WORLD_H, GAME.width, 448, 0x3a2e22)
        .setOrigin(0.5, 1)
        .setDepth(-95);
      wirePan(this, ALUM_WORLD_H);
      this.cameras.main.scrollY = ALUM_BBQ_TOP - 566; // the lawn under the hero (clamped)

      // Live type (signage policy): the banner + the thermometer poster.
      const bannerTxt = this.add
        .text(GAME.width * 0.5, ALUM_BBQ_TOP + Math.round(0.27 * ALUM_BBQ_H), "WELCOME BACK, CLASS OF EVERY QUARTER", {
          fontFamily: "Georgia, serif", fontSize: "54px", fontStyle: "bold",
          color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      if (bannerTxt.width > GAME.width * 0.82) bannerTxt.setScale((GAME.width * 0.82) / bannerTxt.width);
      // Paper patch over the poster's baked gibberish header, live type on top.
      this.add
        .rectangle(GAME.width * 0.305, ALUM_TOP + Math.round(0.415 * ALUM_HALL_H), 240, 145, 0xf7f3e8)
        .setDepth(-41);
      this.add
        .text(GAME.width * 0.315, ALUM_TOP + Math.round(0.39 * ALUM_HALL_H), "GIVING BACK", {
          fontFamily: "Georgia, serif", fontSize: "23px", fontStyle: "bold",
          color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);
      this.add
        .text(GAME.width * 0.315, ALUM_TOP + Math.round(0.428 * ALUM_HALL_H), "(goal: more)", {
          fontFamily: "Georgia, serif", fontSize: "15px",
          color: PALETTE_HEX.charcoal,
        })
        .setOrigin(0.5)
        .setDepth(-40);

      // The owner works the crowd (static bust of the walk-era owner).
      if (isArtLoaded("doll-owner")) {
        const oy = ALUM_BBQ_TOP + Math.round(0.74 * ALUM_BBQ_H);
        this.add
          .image(GAME.width * 0.78, oy, artTextureKey("doll-owner"))
          .setOrigin(0.5, 1)
          .setDepth(oy);
        this.spawnOwnerPitch();
      }

      // Tap gags — regions measured off the boards.
      const taps: Array<{ x0: number; y0: number; x1: number; y1: number; key: string }> = [
        { x0: 0.44 * GAME.width, y0: ALUM_TOP + 0.12 * ALUM_HALL_H, x1: 0.97 * GAME.width, y1: ALUM_TOP + 0.62 * ALUM_HALL_H, key: "alumni-wall" },
        { x0: 0.13 * GAME.width, y0: ALUM_TOP + 0.24 * ALUM_HALL_H, x1: 0.31 * GAME.width, y1: ALUM_TOP + 0.64 * ALUM_HALL_H, key: "alumni-thermo" },
        { x0: 0.53 * GAME.width, y0: ALUM_TOP + 0.58 * ALUM_HALL_H, x1: 0.7 * GAME.width, y1: ALUM_TOP + 0.9 * ALUM_HALL_H, key: "alumni-chips" },
        { x0: 0.71 * GAME.width, y0: ALUM_TOP + 0.56 * ALUM_HALL_H, x1: 0.9 * GAME.width, y1: ALUM_TOP + 0.92 * ALUM_HALL_H, key: "alumni-book" },
        { x0: 0.57 * GAME.width, y0: ALUM_BBQ_TOP + 0.38 * ALUM_BBQ_H, x1: 0.75 * GAME.width, y1: ALUM_BBQ_TOP + 0.58 * ALUM_BBQ_H, key: "alumni-grill" },
        { x0: 0.28 * GAME.width, y0: ALUM_BBQ_TOP + 0.42 * ALUM_BBQ_H, x1: 0.44 * GAME.width, y1: ALUM_BBQ_TOP + 0.64 * ALUM_BBQ_H, key: "alumni-podium" },
      ];
      const onTap = (wx: number, wy: number): void => {
        for (const r of taps) {
          if (wx >= r.x0 && wx <= r.x1 && wy >= r.y0 && wy <= r.y1) {
            const gag = TAP_GAGS[r.key];
            if (gag) showGagModal(gag.title, gag.lines);
            return;
          }
        }
      };
      this.events.on("stagetap", onTap);
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.events.off("stagetap", onTap);
      });
    }

    // Shared hero banner — alumni logotype + cap-&-gown owner + gag pill.
    this.facilityBar = buildHero(this, {
      titleKey: "hero-alumni",
      titleText: "ALUMNI NETWORK",
      ownerKey: "owner-alumni",
      motto: "ONCE A CLIENT, ALWAYS A LEAD",
      theme: HERO_THEME.alumni,
    });
    this.countText = this.add
      .text(GAME.width / 2, HERO.y + HERO.h + 92, "", {
        fontFamily: "monospace",
        fontSize: "30px",
        color: PALETTE_HEX.charcoal,
        backgroundColor: PALETTE_HEX.paper,
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(900);

    this.reconcile();

    this.unsubscribe = runtime.subscribe((events) => {
      if (!this.scene.isActive()) return;
      this.onEvents(events);
      this.reconcile();
    });
    const onWake = () => {
      this.reconcile();
      installAlumniActions(this.overlay, this);
    };
    this.events.on(Phaser.Scenes.Events.WAKE, onWake);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
      this.events.off(Phaser.Scenes.Events.WAKE, onWake);
    });
    installAlumniActions(this.overlay, this);
  }

  private onEvents(events: TickEvent[]): void {
    if (this.facilityBar) fitFacilityName(this.facilityBar); // pick up a live rename
    for (const ev of events) {
      if (ev.type === "alumniReferral") {
        const w = this.pickWalker();
        if (w) this.floatText(w.x, w.y - 340, "I know a guy. he's ready.", PALETTE_HEX.charcoal, 2200);
        this.floatText(GAME.width / 2, ALUM_BBQ_TOP + 120, `${ev.name} referred a friend`, PALETTE_HEX.green);
      } else if (ev.type === "alumniRelapsed") {
        // The quiet beat: one of them walks off toward the office. No line.
        const w = this.pickWalker();
        if (w?.active) w.walkTo(-140, w.y, () => w.destroy());
        this.floatText(GAME.width / 2, ALUM_BBQ_TOP + 120, `${ev.name} is back in the queue…`, PALETTE_HEX.coralRed);
      } else if (ev.type === "contactCallMade") {
        this.ringSomeone();
      }
    }
  }

  private pickWalker(): Character | undefined {
    const live = this.walkers.filter((w) => w.active);
    return live[Phaser.Math.Between(0, Math.max(0, live.length - 1))];
  }

  private reconcile(): void {
    const roster = runtime.state.run.alumni.roster;
    // roster[0] holds the podium (dollhouse only) — the walking crowd draws
    // from roster[1..]; with one alum the speaker is the whole scene.
    const speakerTakesOne = this.dollhouseActive && roster.length > 0;
    const pool = speakerTakesOne ? roster.slice(1) : roster;
    const target = Math.min(pool.length, ALUMNI.visibleSpriteCap);
    this.walkers = this.walkers.filter((w) => w.active);
    while (this.walkers.length < target) {
      const a = pool[this.walkers.length];
      const c = new Character(
        this,
        Phaser.Math.Between(140, GAME.width - 140),
        this.dollhouseActive
          ? Phaser.Math.Between(LAWN_TOP, LAWN_BOTTOM)
          : Phaser.Math.Between(FALLBACK_TOP, FALLBACK_BOTTOM),
        a ? resolveAlumniTexture(a.id) : "person-staff",
      );
      this.wander(c);
      this.walkers.push(c);
    }
    while (this.walkers.length > target) {
      this.walkers.pop()?.destroy();
    }
    // The testimonial: the podium FOLLOWS roster[0] — leader change
    // destroys + recreates; an emptied roster (or absent panels) clears it.
    // Gate on dollhouseActive (both panels), not one isArtLoaded — a
    // partial load must not double-render roster[0] (leg-4 Major, PR #20).
    const podium = speakerTakesOne ? roster[0] : undefined;
    if (!podium && this.speaker) {
      this.speaker.destroy();
      this.speaker = null;
      this.speakerId = null;
    } else if (podium && this.speakerId !== podium.id) {
      this.speaker?.destroy();
      const sy = ALUM_BBQ_TOP + Math.round(0.575 * ALUM_BBQ_H);
      this.speaker = this.add
        .image(GAME.width * 0.29, sy, resolveAlumniTexture(podium.id))
        .setOrigin(0.5, 1)
        .setDepth(sy);
      this.speakerId = podium.id;
      this.spawnTestimonial();
    }
    const extra = pool.length - target;
    this.countText?.setText(
      roster.length === 0
        ? "nobody has finished the full continuum yet"
        : `${roster.length} alumni${extra > 0 ? ` (${extra} beyond the fence)` : ""}`,
    );
  }

  /** The eternal story at the podium (visual RNG; never the sim's). */
  private testimonialRunning = false;
  private spawnTestimonial(): void {
    if (this.testimonialRunning) return;
    this.testimonialRunning = true;
    let line = Phaser.Math.Between(0, ALUMNI_TESTIMONIAL.length - 1);
    const share = (): void => {
      const s = this.speaker;
      if (!this.scene.isActive() || !s?.active) {
        // The chain dies with its speaker — release the lock so the NEXT
        // podium holder can restart the loop (leg-4 Major, PR #20).
        this.testimonialRunning = false;
        return;
      }
      this.floatText(s.x, s.y - 380, ALUMNI_TESTIMONIAL[line % ALUMNI_TESTIMONIAL.length]!, PALETTE_HEX.charcoal, 2400);
      line += 1;
      this.time.delayedCall(Phaser.Math.Between(7000, 11000), share);
    };
    this.time.delayedCall(3000, share);
  }

  /** The owner's booster pitch, delivered to whoever's nearest. */
  private pitchRunning = false;
  private spawnOwnerPitch(): void {
    if (this.pitchRunning) return;
    this.pitchRunning = true;
    let line = Phaser.Math.Between(0, OWNER_ALUMNI_PITCH.length - 1);
    const oy = ALUM_BBQ_TOP + Math.round(0.74 * ALUM_BBQ_H);
    const pitch = (): void => {
      if (!this.scene.isActive()) return;
      this.floatText(GAME.width * 0.78, oy - 420, OWNER_ALUMNI_PITCH[line % OWNER_ALUMNI_PITCH.length]!, PALETTE_HEX.charcoal, 2400);
      line += 1;
      this.time.delayedCall(Phaser.Math.Between(8500, 12500), pitch);
    };
    this.time.delayedCall(5000, pitch);
  }

  /** Ambient milling on the lawn — visual randomness only (never the sim RNG). */
  private wander(c: Character): void {
    if (!c.active) return;
    const x = Phaser.Math.Between(140, GAME.width - 140);
    const y = this.dollhouseActive
      ? Phaser.Math.Between(LAWN_TOP, LAWN_BOTTOM)
      : Phaser.Math.Between(FALLBACK_TOP, FALLBACK_BOTTOM);
    c.walkTo(x, y, () => {
      this.time.delayedCall(Phaser.Math.Between(1200, 5200), () => this.wander(c));
    });
  }

  private ringSomeone(): void {
    const target = this.pickWalker();
    if (target?.active) {
      this.floatText(target.x, target.y - 320, "☎ ring ring", PALETTE_HEX.teal);
    } else {
      this.floatText(GAME.width / 2, ALUM_BBQ_TOP + 120, "☎ calling…", PALETTE_HEX.teal);
    }
  }

  floatText(x: number, y: number, msg: string, color: string, holdMs = 0): void {
    const t = this.add
      .text(x, y, msg, {
        fontFamily: "Georgia, serif",
        fontSize: "40px",
        fontStyle: "bold",
        color,
        ...(holdMs > 0 ? { backgroundColor: PALETTE_HEX.paper, padding: { x: 14, y: 8 } } : {}),
      })
      .setOrigin(0.5, 1)
      .setDepth(3000);
    if (t.width > GAME.width - 60) t.setScale((GAME.width - 60) / t.width);
    const half = t.displayWidth / 2 + 20;
    t.setX(Phaser.Math.Clamp(x, half, GAME.width - half));
    this.tweens.add({
      targets: t,
      y: y - 90,
      alpha: 0,
      delay: holdMs,
      duration: 1000,
      ease: "Cubic.easeOut",
      onComplete: () => t.destroy(),
    });
  }
}
